// biome-ignore-all lint/suspicious/noExplicitAny: We need any here
import { type ReactNode, useMemo } from 'react'
import { FormattedMessage as FMessage, useIntl as useReactIntl } from 'react-intl'
import { postTranslationSelect, useTranslationEdit } from './TranslationEditContext'
import {
  FOCALAZY_EDIT_URL,
  getTranslationDebugSettings,
  INLINE_EDIT_EVENT,
  type InlineEditEventDetail,
} from './translationDebug'

const openInlineEditPopup = (id: string, target: Element) => {
  const rect = target.getBoundingClientRect()
  const detail: InlineEditEventDetail = {
    id,
    anchor: {
      x: rect.left,
      y: rect.top,
      width: rect.width,
      height: rect.height,
    },
  }
  window.dispatchEvent(new CustomEvent(INLINE_EDIT_EVENT, { detail }))
}

type Message<K extends string, W extends string> = {
  id: string
  defaultMessage: string
  description?: string
  values?: { [key in K]: string | true }
  wrappers?: { [key in W]: string | true }
}

// Every entry must be a Message<> object — string shorthand is not accepted.
// Call sites that used to spread enum-derived string arrays need to map them
// to `{ id, defaultMessage }` first.
type ObjectFromList<K extends string, W extends string, T extends readonly Message<K, W>[]> = {
  [P in T[number]['id']]: Extract<T[number], { id: P }>
}

const listToObject = <K extends string, W extends string, T extends readonly Message<K, W>[]>(
  namespace: string,
  list: T
): ObjectFromList<K, W, T> =>
  list.reduce(
    (acc, entry) => ({
      ...acc,
      [entry.id]: { ...entry, id: `${namespace}.${entry.id}` },
    }),
    {} as ObjectFromList<K, W, T>
  )

// ---------------------------------------------------------------------------
// Enum-keyed defineMessages (1D / 2D / matrix).
//
// Lets a map be keyed by an enum (or string-union via a values tuple) with
// exhaustiveness enforced, the source copy written inline, and shared
// values/wrappers declared once via a wrapper on the enum argument. The runtime
// object stays keyed by the RAW enum value; the generated `id` keeps each
// segment's casing (enum-derived leaves stay PascalCase) and only forces the
// whole id's first char lowercase (see `buildFullId`).
// See .claude/specs/definemessages-enum-maps.md.
// ---------------------------------------------------------------------------

type EnumLike = Record<string, string>

type ValuesOf<E> = E extends readonly (infer V extends string)[]
  ? V
  : E extends EnumLike
    ? E[keyof E]
    : never

type Meta<K extends string = never, W extends string = never> = {
  values?: { [key in K]: string | true }
  wrappers?: { [key in W]: string | true }
}

// The built message handed to consumers. `values`/`wrappers` are PRESENT
// (required) only when K/W are non-never — not optional like Message<K,W> — so
// FormattedMessage's `M extends { wrappers: any }` branch fires and requires the
// matching prop. (Compile-verified.)
type BuiltMessage<K extends string, W extends string> = {
  id: string
  defaultMessage: string
  description?: string
} & ([K] extends [never] ? unknown : { values: { [key in K]: string | true } }) &
  ([W] extends [never] ? unknown : { wrappers: { [key in W]: string | true } })

// Runtime symbols double as type-level brands so the overloads can tell a
// decorated enum apart from a bare one. Module-private (not exported).
const VARIABLES = Symbol('faceup.localization.withVariables')
const PER_FIELD_VARIABLES = Symbol('faceup.localization.withPerFieldVariables')
const MATRIX = Symbol('faceup.localization.withMatrix')

type WithVariables<E, K extends string, W extends string> = E & {
  readonly [VARIABLES]: Meta<K, W>
}
type WithPerFieldVariables<E, S> = E & { readonly [PER_FIELD_VARIABLES]: S }
type WithMatrix<O, I, K extends string, W extends string> = O & {
  readonly [MATRIX]: { inner: I; meta: Meta<K, W> }
}

// Attached to `defineMessages` as members below (defineMessages.withVariables
// etc.); not exported standalone.

/** Declare `values`/`wrappers` shared by every member of a 1D enum (or values-tuple) map. */
const withVariables = <
  E extends EnumLike | readonly string[],
  K extends string = never,
  W extends string = never,
>(
  enumObj: E,
  meta: Meta<K, W>
) => ({ ...enumObj, [VARIABLES]: meta }) as WithVariables<E, K, W>

/** Declare `values`/`wrappers` on specific sub-keys (fields) of a 2D enum map. */
const withPerFieldVariables = <E extends EnumLike, S extends Record<string, Meta<string, string>>>(
  enumObj: E,
  shape: S
) => ({ ...enumObj, [PER_FIELD_VARIABLES]: shape }) as WithPerFieldVariables<E, S>

/** Two-enum (nested) map: outer x inner, with shared cell `values`/`wrappers`. */
const withMatrix = <
  O extends EnumLike,
  I extends EnumLike,
  K extends string = never,
  W extends string = never,
>(
  outer: O,
  inner: I,
  meta?: Meta<K, W>
) => ({ ...outer, [MATRIX]: { inner, meta: meta ?? {} } }) as WithMatrix<O, I, K, W>

// Extract the declared value/wrapper key set for a sub-key from a
// withPerFieldVariables descriptor `S`. Matches only when `values`/`wrappers` is actually present
// (every object structurally matches `Meta<infer, infer>`, so inferring from
// that would wrongly yield `string` for an undeclared slot).
type MetaValues<S, K> = K extends keyof S
  ? S[K] extends { values: infer V }
    ? keyof V & string
    : never
  : never
type MetaWrappers<S, K> = K extends keyof S
  ? S[K] extends { wrappers: infer V }
    ? keyof V & string
    : never
  : never

// MUST match _translationAst.mjs `buildFullId` byte-for-byte (focalazy leaf-key
// rule). Segments keep their casing; only the whole id's first char is forced
// lowercase. Applied only to the id, never to the keys the returned object is
// indexed by.
const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1)
const buildFullId = (namespace: string, segments: string[]) =>
  lowerFirst(`${namespace}.${segments.join('.')}`)

// One recursive builder for 1D / 2D / matrix. Object value -> recurse (append a
// segment); string value -> leaf message. Keyed by the raw map keys.
const buildEnumMessages = (namespace: string, decorated: any, map: any): any => {
  const shape = decorated[PER_FIELD_VARIABLES]
  const uniformMeta = decorated[VARIABLES] ?? decorated[MATRIX]?.meta
  const build = (node: any, segments: string[]): any => {
    // A `null` leaf means "this member deliberately has no message for this
    // sub-key". Keep it null at runtime (the return type maps it to `null` too),
    // and the extractor skips it — no id is generated. Guard before the object
    // branch: `typeof null === 'object'`, so `Object.keys(null)` would throw.
    if (node === null) {
      return null
    }
    if (typeof node === 'string') {
      const leafKey = segments[segments.length - 1]
      const meta = uniformMeta ?? (leafKey === undefined ? undefined : shape?.[leafKey])
      return {
        id: buildFullId(namespace, segments),
        defaultMessage: node,
        ...(meta?.values ? { values: meta.values } : {}),
        ...(meta?.wrappers ? { wrappers: meta.wrappers } : {}),
      }
    }
    return Object.fromEntries(
      Object.keys(node).map(key => [key, build(node[key], [...segments, key])])
    )
  }
  return build(map, [])
}

// 2D + meta (withPerFieldVariables) — branded overloads first; a
// WithPerFieldVariables/WithMatrix/WithVariables value also structurally extends
// EnumLike, so its overload must win. The whole map `M` is inferred (so sub-keys
// stay literal, not widened to `string`); per-sub meta is read from the
// withPerFieldVariables descriptor `S`.
function defineMessagesImpl<
  E extends EnumLike,
  S extends Record<string, Meta<string, string>>,
  M extends Record<ValuesOf<E>, Record<string, string>>,
>(
  namespace: string,
  enumWithPerFieldVariables: WithPerFieldVariables<E, S>,
  map: M & Record<ValuesOf<E>, Record<string, string>>
): {
  [Member in keyof M]: {
    [Sub in keyof M[Member]]: BuiltMessage<MetaValues<S, Sub>, MetaWrappers<S, Sub>>
  }
}
// matrix (withMatrix) — outer x inner. Both keys are known enum unions, so the
// direct nested Record both enforces exhaustiveness on each axis AND indexes
// cleanly (no inferred sub-key to widen).
function defineMessagesImpl<
  O extends EnumLike,
  I extends EnumLike,
  K extends string,
  W extends string,
>(
  namespace: string,
  enumWithMatrix: WithMatrix<O, I, K, W>,
  map: Record<ValuesOf<O>, Record<ValuesOf<I>, string>>
): Record<ValuesOf<O>, Record<ValuesOf<I>, BuiltMessage<K, W>>>
// 1D + shared meta (withVariables)
function defineMessagesImpl<
  E extends EnumLike | readonly string[],
  K extends string,
  W extends string,
>(
  namespace: string,
  enumWithVariables: WithVariables<E, K, W>,
  map: Record<ValuesOf<E>, string>
): Record<ValuesOf<E>, BuiltMessage<K, W>>
// 2D plain — sub-keys inferred (literal) from the object map; the `& Record<...>`
// intersection enforces outer-enum exhaustiveness while M keeps the literal keys.
// A sub-key may be `null` for a member that has no copy for it — the shape then
// stays uniform (every member lists every sub-key) and the return type maps that
// slot to `null`, NOT to `BuiltMessage`. So indexing `messages[member].sub` needs
// a `? :` guard ONLY when at least one member is null for that sub-key; if every
// member has copy, the union collapses to plain `BuiltMessage` (no guard).
function defineMessagesImpl<
  E extends EnumLike | readonly string[],
  M extends Record<ValuesOf<E>, Record<string, string | null>>,
>(
  namespace: string,
  enumOrValues: E,
  map: M & Record<ValuesOf<E>, Record<string, string | null>>
): {
  [Member in keyof M]: {
    [Sub in keyof M[Member]]: M[Member][Sub] extends null ? null : BuiltMessage<never, never>
  }
}
// 1D plain — the value IS the defaultMessage
function defineMessagesImpl<E extends EnumLike | readonly string[]>(
  namespace: string,
  enumOrValues: E,
  map: Record<ValuesOf<E>, string>
): Record<ValuesOf<E>, BuiltMessage<never, never>>
// legacy list — unchanged
function defineMessagesImpl<K extends string, W extends string, T extends readonly Message<K, W>[]>(
  namespace: string,
  messages: T
): ObjectFromList<K, W, T>
function defineMessagesImpl(namespace: string, second: any, third?: any): any {
  // Discriminate by arg COUNT, not `Array.isArray(second)`: the legacy list form
  // is 2-arg (no map) with an array second arg, but a 3-arg enum-keyed call can
  // ALSO pass an array as second (a values tuple / `zodEnum.options`), and that
  // must build from the map (third), not be mistaken for a legacy list.
  if (third === undefined && Array.isArray(second)) {
    return listToObject(namespace, second)
  }
  return buildEnumMessages(namespace, second, third)
}

// Attach the enum-map decorators as members so call sites only import
// `defineMessages` (defineMessages.withVariables / .withPerFieldVariables /
// .withMatrix). The explicit intersection preserves every overload above.
export const defineMessages: typeof defineMessagesImpl & {
  withVariables: typeof withVariables
  withPerFieldVariables: typeof withPerFieldVariables
  withMatrix: typeof withMatrix
} = Object.assign(defineMessagesImpl, { withVariables, withPerFieldVariables, withMatrix })

const getDefaultMessage = (
  messageId: string,
  values?: Record<string, any>,
  wrappers?: Record<string, any>,
  useFullId?: boolean
): string => {
  const defaultMessage = useFullId ? messageId : (messageId.split('.').pop() ?? messageId)
  const parts: string[] = [defaultMessage]

  if (values && Object.keys(values).length > 0) {
    parts.push(
      `[${Object.keys(values)
        .map(key => `(${key}): "{${key}}"`)
        .join('; ')}]`
    )
  }

  if (wrappers && Object.keys(wrappers).length > 0) {
    parts.push(
      `[${Object.keys(wrappers)
        .map(key => `<<${key}>${key}</${key}>>`)
        .join('; ')}]`
    )
  }

  return parts.join(' ')
}

const getMissingPlaceholders = (
  rawMessage: string | undefined,
  values?: Record<string, any>,
  wrappers?: Record<string, any>
): string[] => {
  if (!rawMessage) {
    return []
  }
  const missing: string[] = []
  if (values) {
    for (const key of Object.keys(values)) {
      if (!rawMessage.includes(`{${key}}`)) {
        missing.push(`{${key}}`)
      }
    }
  }
  if (wrappers) {
    for (const key of Object.keys(wrappers)) {
      if (!rawMessage.includes(`<${key}>`)) {
        missing.push(`<${key}>`)
      }
    }
  }
  return missing
}

type FormattedMessagePropsSingle<M> =
  M extends Message<infer K, infer W>
    ? M extends { values: any; wrappers: any }
      ? {
          message: M
          values: { [key in K]: ReactNode }
          wrappers: { [key in W]: (text: ReactNode) => ReactNode }
        }
      : M extends { values: any }
        ? {
            message: M
            values: { [key in K]: ReactNode }
            wrappers?: never
          }
        : M extends { wrappers: any }
          ? {
              message: M
              values?: never
              wrappers: { [key in W]: (text: ReactNode) => ReactNode }
            }
          : {
              message: M
              values?: never
              wrappers?: never
            }
    : M extends { id: string; description?: string }
      ? {
          message: M
          values?: never
          wrappers?: never
        }
      : {
          message: { id: string; description?: string }
          values?: never
          wrappers?: never
        }

type FormattedMessageProps<M> = [M] extends [{ id: string; values?: never; wrappers?: never }]
  ? {
      message: M
      values?: never
      wrappers?: never
    }
  : [M] extends [{ id: string; description?: string; values?: undefined; wrappers?: undefined }]
    ? {
        message: M
        values?: never
        wrappers?: never
      }
    : M extends any
      ? FormattedMessagePropsSingle<M>
      : never

export const FormattedMessage = <
  M extends Message<any, any> | { id: string; description?: string },
>(
  props: FormattedMessageProps<M>
): ReactNode => {
  const { message } = props
  const values = {
    ...(props.values ? props.values : {}),
    ...(props.wrappers ? props.wrappers : {}),
  }

  const debugSettings = getTranslationDebugSettings()
  const intl = useReactIntl()
  const { enabled: editEnabled } = useTranslationEdit()

  const missingWarning = debugSettings.checkVariables
    ? (() => {
        const rawMsg = intl.messages[message.id] as string | undefined
        const missing = getMissingPlaceholders(rawMsg, props.values, props.wrappers)
        if (missing.length === 0) {
          return null
        }
        return (
          <span
            className='translation-debug-trigger text-red-600 font-bold ml-1 cursor-help'
            title={`Missing: ${missing.join(', ')}`}
          >
            {'\u26A0'}
          </span>
        )
      })()
    : null

  const focalazyLink =
    debugSettings.showFocalazyLinks && !editEnabled ? (
      <a
        href={`${FOCALAZY_EDIT_URL}${message.id}`}
        target='_blank'
        rel='noopener noreferrer'
        className='translation-debug-trigger text-[10px] uppercase bg-white px-1 py-0.5 rounded border border-indigo-500 text-indigo-500 ml-1 no-underline hover:bg-indigo-50 transition-colors'
      >
        Edit
      </a>
    ) : null

  const body = debugSettings.showKeys ? (
    getDefaultMessage(message.id, props.values, props.wrappers, true)
  ) : (
    <FMessage
      id={message.id}
      values={values}
      defaultMessage={
        ('defaultMessage' in message && message.defaultMessage) ||
        getDefaultMessage(message.id, props.values, props.wrappers)
      }
    />
  )

  // Two ways to enter edit mode: (1) inside the Focalazy iframe after the
  // postMessage handshake (`editEnabled`); (2) standalone via the DevTools
  // inline-edit toggle (`debugSettings.inlineEdit`). Both share the same
  // Alt/Option-click affordance — the modifier keeps regular clicks on
  // buttons/links working untouched. The two paths only differ in what the
  // click does: iframe path posts to the parent, standalone path opens an
  // anchored popup driven by DevTools.
  const editMode = editEnabled
    ? ('iframe' as const)
    : debugSettings.inlineEdit
      ? ('inline' as const)
      : null
  if (editMode) {
    return (
      <span
        data-translation-id={message.id}
        title='Alt/Option-click to edit translation'
        onClickCapture={event => {
          if (!event.altKey) {
            return
          }
          event.preventDefault()
          event.stopPropagation()
          if (editMode === 'iframe') {
            postTranslationSelect(message.id)
          } else {
            openInlineEditPopup(message.id, event.currentTarget)
          }
        }}
        className='rounded-sm transition-colors underline decoration-dotted decoration-indigo-400/0 underline-offset-4 hover:[&]:decoration-indigo-400/70'
      >
        {body}
        {missingWarning}
      </span>
    )
  }

  const hasTrailingControls = Boolean(focalazyLink || missingWarning)
  if (!hasTrailingControls) {
    return body
  }

  return (
    <span className='inline rounded px-0.5 transition-all has-[.translation-debug-trigger:hover]:bg-indigo-200 has-[.translation-debug-trigger:hover]:ring-2 has-[.translation-debug-trigger:hover]:ring-indigo-400 has-[.translation-debug-trigger:hover]:shadow-md'>
      {body}
      {focalazyLink}
      {missingWarning}
    </span>
  )
}

export const useIntl = () => {
  const intl = useReactIntl()

  function formatMessage<K extends string, W extends string>(
    message: Message<K, W> & { values: any; wrappers: any },
    options: {
      values: { [key in K]: string | number }
      wrappers: { [key in W]: (text: string) => string }
    }
  ): string
  function formatMessage<K extends string, W extends string>(
    message: Message<K, W> & { values: any } & { wrappers?: never },
    options: {
      values: { [key in K]: string | number }
    }
  ): string
  function formatMessage<K extends string, W extends string>(
    message: Message<K, W> & { wrappers: any } & { values?: never },
    options: {
      wrappers: { [key in W]: (text: string) => string }
    }
  ): string
  function formatMessage<K extends string, W extends string>(
    message: Message<K, W> & { values?: never; wrappers?: never }
  ): string
  function formatMessage(message: {
    id: string
    description?: string
    values?: never
    wrappers?: never
  }): string
  function formatMessage(message: any, options?: any): string {
    const values = {
      ...(options?.values ? options.values : {}),
      ...(options?.wrappers ? options.wrappers : {}),
    }
    // With no values/wrappers, pass `undefined` (not `{}`) so react-intl returns
    // the raw pattern for messages holding ICU args or rich-text tags instead of
    // failing to format and falling back to the id leaf. Callers that post-process
    // the raw string (e.g. `formatMessage(msg).split('{name}')`) rely on this.
    const formatValues = Object.keys(values).length > 0 ? values : undefined

    const debugSettings = getTranslationDebugSettings()

    const text = debugSettings.showKeys
      ? getDefaultMessage(message.id, options?.values, options?.wrappers, true)
      : intl.formatMessage(
          {
            id: message.id,
            defaultMessage:
              ('defaultMessage' in message && message.defaultMessage) ||
              getDefaultMessage(message.id, options?.values, options?.wrappers),
          },
          formatValues
        )

    const missingMark = debugSettings.checkVariables
      ? (() => {
          const rawMsg = intl.messages[message.id] as string | undefined
          const missing = getMissingPlaceholders(rawMsg, options?.values, options?.wrappers)
          return missing.length > 0 ? ' [!]' : ''
        })()
      : ''

    const focalazyLink = debugSettings.showFocalazyLinks
      ? ` [${FOCALAZY_EDIT_URL}${message.id}]`
      : ''

    return `${text}${missingMark}${focalazyLink}`
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies(formatMessage): new each render; its only input (intl) is already a dep
  return useMemo(
    () => ({
      ...intl,
      formatMessage,
    }),
    [intl]
  )
}
